export type ExplorerEntity = 'address' | 'tx';

const TRONSCAN_EXPLORER_HOSTS = new Set([
  'tronscan.org',
  'www.tronscan.org',
  'nile.tronscan.org',
  'shasta.tronscan.org',
]);

function parseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function isTronscanExplorerUrl(url: URL): boolean {
  return TRONSCAN_EXPLORER_HOSTS.has(url.hostname.toLowerCase());
}

function isSolscanUrl(url: URL): boolean {
  return url.hostname.toLowerCase() === 'solscan.io';
}

export function isTronscanExplorerBaseUrl(baseUrl: string | null | undefined): boolean {
  if (typeof baseUrl !== 'string') {
    return false;
  }
  const parsed = parseUrl(baseUrl.trim());
  return Boolean(parsed && isTronscanExplorerUrl(parsed));
}

export function normalizeExplorerBaseUrl(baseUrl?: string | null): string | undefined {
  if (typeof baseUrl !== 'string') {
    return undefined;
  }

  const trimmed = baseUrl.trim();
  if (!trimmed) {
    return undefined;
  }

  const parsed = parseUrl(trimmed);
  if (parsed && isTronscanExplorerUrl(parsed)) {
    parsed.hash = '';
    parsed.search = '';
    parsed.pathname = parsed.pathname.replace(/\/+$/u, '');
    if (parsed.pathname === '') {
      parsed.pathname = '/';
    }
    return parsed.toString().replace(/\/$/u, '');
  }

  return trimmed.replace(/\/+$/u, '');
}

function buildTronscanEntityUrl(baseUrl: URL, entity: ExplorerEntity, value: string): string {
  const encodedValue = encodeURIComponent(value);
  const path =
    entity === 'tx'
      ? `/transaction/${encodedValue}/overview`
      : `/address/${encodedValue}/transactions`;
  return `${baseUrl.origin}${path}`;
}

function buildTronscanTokenUrl(baseUrl: URL, value: string): string {
  return `${baseUrl.origin}/token20/${encodeURIComponent(value)}/transfers`;
}

function buildUrlWithPath(
  baseUrl: URL,
  segment: string,
  value: string,
): string {
  const basePath = baseUrl.pathname.replace(/\/+$/u, '');
  const encodedValue = encodeURIComponent(value);
  baseUrl.pathname = `${basePath.length > 0 ? basePath : ''}/${segment}/${encodedValue}`;
  baseUrl.hash = '';
  return baseUrl.toString();
}

export function buildExplorerEntityUrl(
  baseUrl: string | undefined | null,
  entity: ExplorerEntity,
  value: string | undefined | null,
): string | undefined {
  const normalizedBase = normalizeExplorerBaseUrl(baseUrl);
  const normalizedValue = typeof value === 'string' ? value.trim() : '';
  if (!normalizedBase || !normalizedValue) {
    return undefined;
  }

  const parsed = parseUrl(normalizedBase);
  if (!parsed) {
    return undefined;
  }

  if (isTronscanExplorerUrl(parsed)) {
    return buildTronscanEntityUrl(parsed, entity, normalizedValue);
  }

  const segment = isSolscanUrl(parsed) && entity === 'address' ? 'account' : entity;
  return buildUrlWithPath(parsed, segment, normalizedValue);
}

export function buildTokenExplorerUrl(
  compatibility: string | undefined | null,
  baseUrl: string | undefined | null,
  tokenAddress: string | undefined | null,
): string | undefined {
  const normalizedBase = normalizeExplorerBaseUrl(baseUrl);
  const normalizedAddress = typeof tokenAddress === 'string' ? tokenAddress.trim() : '';
  if (!normalizedBase || !normalizedAddress) {
    return undefined;
  }

  const parsed = parseUrl(normalizedBase);
  if (!parsed) {
    return undefined;
  }

  const normalizedCompatibility = compatibility?.trim().toLowerCase();
  if (normalizedCompatibility === 'tron') {
    if (isTronscanExplorerUrl(parsed)) {
      return buildTronscanTokenUrl(parsed, normalizedAddress);
    }
    return buildUrlWithPath(parsed, 'token20', normalizedAddress);
  }

  if (normalizedCompatibility === 'solana') {
    const segment = isSolscanUrl(parsed) ? 'account' : 'address';
    return buildUrlWithPath(parsed, segment, normalizedAddress);
  }

  return buildUrlWithPath(parsed, 'token', normalizedAddress);
}

export function normalizeKnownExplorerUrl(value: string | undefined | null): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const parsed = parseUrl(trimmed);
  if (!parsed || !isTronscanExplorerUrl(parsed)) {
    return trimmed;
  }

  const legacyPath = parsed.hash.startsWith('#/')
    ? parsed.hash.slice(1)
    : parsed.pathname;
  const segments = legacyPath.split('/').filter(Boolean);
  const [kind, rawValue] = segments;

  if (!kind || !rawValue) {
    return normalizeExplorerBaseUrl(trimmed);
  }

  if (kind === 'transaction') {
    return buildTronscanEntityUrl(parsed, 'tx', decodeURIComponent(rawValue));
  }

  if (kind === 'address') {
    return buildTronscanEntityUrl(parsed, 'address', decodeURIComponent(rawValue));
  }

  if (kind === 'token20') {
    return buildTronscanTokenUrl(parsed, decodeURIComponent(rawValue));
  }

  return normalizeExplorerBaseUrl(trimmed);
}
